import { useEffect, useState } from "react"
import { router } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"

type Props = {
  open: boolean
  onClose: () => void
  pieceId: number
  // Slightly different copy for "Published?" (manual-publish, expected
  // outcome) vs "Already published?" (out-of-band capture from any state).
  variant?: "expected" | "out_of_band"
  // Pre-fill the URL field with whatever was previously captured (e.g.
  // updating an already-published piece's URL).
  initialUrl?: string | null
}

// URL capture modal. Used by:
//   * "Published?" — the manual-publish "yes, I published it" confirmation.
//   * "Already published?" — out-of-band capture from queued/scheduled/published.
//
// Submits to the piece's action dispatcher with the `mark_published_externally`
// action; the server destroys any existing slot, closes open todos, and
// transitions the piece to `published` with the supplied URL.
export function MarkPublishedDialog({ open, onClose, pieceId, variant = "expected", initialUrl }: Props) {
  const [url, setUrl] = useState(initialUrl ?? "")
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (open) {
      setUrl(initialUrl ?? "")
      setError(null)
    }
  }, [open, initialUrl])

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = url.trim()

    setSubmitting(true)
    router.post(
      `/pieces/${pieceId}/actions/mark_published_externally`,
      { piece: { published_url: trimmed } },
      {
        preserveScroll: true,
        onSuccess: () => {
          setSubmitting(false)
          onClose()
        },
        onError: (errors) => {
          setSubmitting(false)
          setError((errors as Record<string, string>)?.base ?? "Couldn't mark as published.")
        },
      },
    )
  }

  const title = variant === "expected" ? "Mark as published" : "Already published?"
  const desc = variant === "expected"
    ? "Paste the published URL so SparkDrop has the live link recorded. You can leave it blank and add it later."
    : "Capture an out-of-band publish. SparkDrop will move the piece to Published, destroy any pending slot, and close open todos."

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{desc}</DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="flex flex-col gap-3">
          <div>
            <label htmlFor="mp-url">Published URL (optional)</label>
            <Input
              id="mp-url"
              type="url"
              inputMode="url"
              placeholder="https://x.com/buildermethods/status/…"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              autoFocus
            />
          </div>
          {error && <p className="text-sm text-danger">{error}</p>}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? "Saving…" : "Mark as published"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
